/* Önizlemenin içindeki düzenleyici — blok çubuğu, yerinde yazma, biçim çubuğu.

   Önizleme aynı kökenli bir `srcdoc` iframe'i; panel onun belgesine
   doğrudan erişiyor. Bu modül o belgeye dinleyiciler ve birkaç araç öğesi
   (`.fg-ui`) ekliyor; belgeyi kendisi değiştirmiyor. Her değişiklik
   kabuğa (views/design.ts) iki yoldan biriyle gidiyor:

     commitText   bir metin alanı yazıldı — sayfa yeniden çizilmiyor, imleç
                  yerinde kalıyor. HTML önce temizleniyor (sanitizeRich).
     mutate       yapı değişti (blok eklendi, silindi, taşındı) — kabuk
                  belgeyi değiştirip sayfayı yeniden çiziyor; bu modül
                  yükleme bitince seçimi ve imleci geri koyuyor.

   **Belgeye DOM'dan hiçbir şey ham girmiyor.** contenteditable tarayıcıya
   göre farklı HTML üretiyor (Enter'da `<div>`, kalında `<b>`, renkte
   `<span style="color: rgb(…)">`); temizleyici hepsini aynı kanonik biçime
   indiriyor ve izinli olmayanı atıyor. */

import { isFixed, safeUrl, sanitizePlain, sanitizeRich, TEXT_SIZES, type Block } from '@frawgraphy/shared';
import { el } from '../dom.ts';
import { CATALOG, TYPE_LABELS, type CatalogEntry } from './blank.ts';
import { locate } from './doc.ts';

export type Focus = { blockId: string; path: string; at: 'start' | 'end' };

export type CanvasHost = {
  frame: HTMLIFrameElement;
  home(): Block[];
  lang(): 'tr' | 'en';
  selected(): string | null;
  select(id: string | null): void;
  commitText(blockId: string, path: string, html: string): void;
  /** Yapısal değişiklik. `fn` yeni seçilecek blok kimliğini döndürebilir. */
  mutate(fn: (home: Block[]) => string | null | void, after?: { focus?: Focus; select?: string | null }): void;
  insert(type: CatalogEntry['type'], afterId: string | null, replaceId?: string): void;
  itemOp(blockId: string, itemId: string, op: 'duplicate' | 'remove' | 'up' | 'down'): void;
  confirmRemove(block: Block): boolean;
};

/** Tek satırlık, biçimsiz alanlar: biçim çubuğu bunlarda kapalı. */
function isPlain(block: Block | undefined, path: string): boolean {
  if (!block) return true;
  const last = path.split('.').pop() ?? '';
  if (['label', 'spec', 'eyebrow', 'hint', 'category', 'alt', 'time'].includes(last)) return true;
  if (block.type === 'reels' && last === 'title') return true;
  return false;
}

/** Enter'ın yeni bir paragraf bloğu açtığı alanlar. */
const SPLITTABLE = new Set(['paragraph', 'heading', 'eyebrow', 'note', 'quote']);

export function attachCanvas(host: CanvasHost) {
  let doc: Document | null = null;
  let win: Window | null = null;
  let editing: { el: HTMLElement; blockId: string; path: string; timer?: ReturnType<typeof setTimeout> } | null = null;
  let pendingFocus: Focus | null = null;
  let hoverId: string | null = null;
  let blockBar: HTMLElement | null = null;
  let itemBar: HTMLElement | null = null;
  let formatBar: HTMLElement | null = null;
  let menu: HTMLElement | null = null;
  let drop: HTMLElement | null = null;
  let drag: { id: string; target: string | null; after: boolean } | null = null;

  const blockOf = (id: string | null) => (id ? locate(host.home(), id)?.block : undefined);

  function position(node: HTMLElement, rect: DOMRect, place: 'above-left' | 'above-right' | 'below-left') {
    if (!win) return;
    const sx = win.scrollX;
    const sy = win.scrollY;
    const w = node.offsetWidth;
    const h = node.offsetHeight;
    const maxX = (doc?.documentElement.clientWidth ?? 1000) - w - 4;
    let x = place === 'above-right' ? rect.right - w : rect.left;
    let y = place === 'below-left' ? rect.bottom + 6 : rect.top - h - 6;
    /* Ekranın üstüne taşan çubuk bloğun içine iner. */
    if (y < 4) y = place === 'below-left' ? y : rect.top + 4;
    x = Math.max(4, Math.min(x, maxX));
    node.style.left = `${x + sx}px`;
    node.style.top = `${y + sy}px`;
  }

  /* ── Yükleme ─────────────────────────────────────────── */

  function onLoad(): void {
    doc = host.frame.contentDocument;
    win = host.frame.contentWindow;
    if (!doc || !win) return;
    editing = null;
    blockBar = itemBar = formatBar = menu = drop = null;
    doc.addEventListener('mousemove', onMove);
    doc.addEventListener('mouseleave', () => {
      clearTimeout(blockTimer);
      blockTimer = setTimeout(() => showBlockBar(host.selected()), GRACE_MS);
    });
    doc.addEventListener('click', onClick, true);
    /* Bırakma tuşun bırakıldığı an: tıklama olayı tutamaktan başka bir
       öğede bitince her tarayıcıda gelmiyor. */
    doc.addEventListener('mouseup', () => drag && endDrag(), true);
    doc.addEventListener('submit', (e) => e.preventDefault(), true);
    doc.addEventListener('keydown', onKey, true);
    doc.addEventListener('paste', onPaste, true);
    doc.addEventListener('selectionchange', () => editing && placeFormatBar());
    doc.addEventListener('focusout', onFocusOut, true);
    win.addEventListener('scroll', () => {
      showBlockBar(hoverId ?? host.selected());
      if (editing) placeFormatBar();
    });
    markSelected();
    if (pendingFocus) {
      const f = pendingFocus;
      pendingFocus = null;
      const node = findField(f.blockId, f.path);
      if (node) {
        startEditing(node, f.blockId, f.path);
        caretTo(node, f.at);
        node.scrollIntoView({ block: 'nearest' });
      }
    } else {
      showBlockBar(host.selected());
    }
  }

  function findField(blockId: string, path: string): HTMLElement | null {
    const root = doc?.querySelector<HTMLElement>(`[data-b="${CSS.escape(blockId)}"]`);
    if (!root) return null;
    if (root.getAttribute('data-f') === path) return root;
    return [...root.querySelectorAll<HTMLElement>(`[data-f="${CSS.escape(path)}"]`)].find((n) => n.closest('[data-b]') === root) ?? null;
  }

  function markSelected(): void {
    if (!doc) return;
    doc.querySelectorAll('.fg-selected').forEach((n) => n.classList.remove('fg-selected'));
    const id = host.selected();
    if (id) doc.querySelector(`[data-b="${CSS.escape(id)}"]`)?.classList.add('fg-selected');
  }

  /* ── Üzerine gelme ve çubuklar ───────────────────────── */

  /**
   * Çubukların bekleme süresi. Blok çubuğu bloğun üstünde duruyor; fare
   * oraya giderken arada çoğunlukla kapsayan bölümün üstünden geçiyor. Çubuk
   * o an yeni bloğa taşınsaydı fare hiçbir zaman ona ulaşamazdı (kullanıcı
   * bildirdi, 2026-10-18). Geçiş bu kadar bekliyor; fare bu sürede çubuğa
   * girerse ya da kendi bloğuna dönerse iptal oluyor.
   */
  const GRACE_MS = 600;
  let moveFrame = 0;
  let blockTimer: ReturnType<typeof setTimeout> | undefined;
  let itemTimer: ReturnType<typeof setTimeout> | undefined;
  let shownBlock: string | null = null;
  let shownItem: HTMLElement | null = null;

  function onMove(e: MouseEvent): void {
    if (drag) return dragMove(e);
    const target = e.target as Element | null;
    if (!target) return;
    /* Fare bir çubuğun üstünde: bekleyen bütün geçişler iptal, çubuk kalır. */
    if (target.closest('.fg-ui')) {
      clearTimeout(blockTimer);
      clearTimeout(itemTimer);
      return;
    }
    cancelAnimationFrame(moveFrame);
    moveFrame = requestAnimationFrame(() => {
      const block = target.closest<HTMLElement>('[data-b]');
      const id = block?.dataset['b'] ?? null;
      hoverId = id;
      const wanted = id ?? host.selected();
      if (wanted === shownBlock) {
        clearTimeout(blockTimer);
      } else {
        clearTimeout(blockTimer);
        /* Hiç çubuk yoksa beklemeye gerek yok: ilk görünüş anında. */
        blockTimer = setTimeout(() => showBlockBar(wanted), blockBar ? GRACE_MS : 0);
      }
      const item = target.closest<HTMLElement>('[data-i]');
      if (item === shownItem) {
        clearTimeout(itemTimer);
      } else {
        clearTimeout(itemTimer);
        itemTimer = setTimeout(() => showItemBar(item), itemBar && !item ? GRACE_MS : itemBar ? GRACE_MS / 2 : 0);
      }
    });
  }

  function showBlockBar(id: string | null): void {
    if (!doc) return;
    blockBar?.remove();
    blockBar = null;
    shownBlock = id;
    const block = blockOf(id);
    const node = id ? doc.querySelector<HTMLElement>(`[data-b="${CSS.escape(id)}"]`) : null;
    if (!block || !node || !id) return;
    const btn = (text: string, title: string, run: () => void, disabled = false) =>
      el('button', {
        type: 'button',
        text,
        title,
        disabled,
        onmousedown: (e: Event) => e.preventDefault(),
        onclick: (e: Event) => {
          e.stopPropagation();
          run();
        }
      });
    const loc = locate(host.home(), id)!;
    /* Menü, footer ve sayfa bölgesi yerinden oynamıyor: yalnızca ayarlar
       ve (sayfa bölgesinde) içine blok ekleme. */
    if (isFixed(block)) {
      blockBar = el(
        'div',
        { class: 'fg-ui fg-bar' },
        el('span', { class: 'fg-tag', text: TYPE_LABELS[block.type] }),
        el('span', { class: 'fg-sep' }),
        block.type === 'page' ? btn('+', 'Bu sayfaya blok ekle', () => openMenu(node, id)) : null,
        btn('⚙', 'Ayarlar', () => host.select(id))
      );
      doc.body.append(blockBar);
      position(blockBar, node.getBoundingClientRect(), 'above-left');
      return;
    }
    const handle = el('span', { class: 'fg-tag', text: `⠿ ${TYPE_LABELS[block.type]}`, title: 'Sürükleyerek taşı' });
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      drag = { id, target: null, after: false };
      doc!.body.style.cursor = 'grabbing';
    });
    blockBar = el(
      'div',
      { class: 'fg-ui fg-bar' },
      handle,
      el('span', { class: 'fg-sep' }),
      btn('+', 'Altına blok ekle', () => openMenu(node, id)),
      btn('⧉', 'Çoğalt', () => host.mutate((home) => duplicateOp(home, id))),
      btn('↑', 'Yukarı', () => host.mutate((home) => moveOp(home, id, -1)), loc.index === 0),
      btn('↓', 'Aşağı', () => host.mutate((home) => moveOp(home, id, 1)), loc.index === loc.list.length - 1),
      btn('⚙', 'Ayarlar', () => host.select(id)),
      btn('✕', 'Sil', () => {
        if (!host.confirmRemove(block)) return;
        host.mutate((home) => {
          const at = locate(home, id);
          at?.list.splice(at.index, 1);
          return null;
        }, { select: null });
      })
    );
    doc.body.append(blockBar);
    position(blockBar, node.getBoundingClientRect(), 'above-left');
  }

  /* Çoğaltma ve taşıma kabuktaki doc.ts işlemlerini çağırıyor; burada
     yalnızca kimlik akışı. */
  let duplicateOp: (home: Block[], id: string) => string | null = () => null;
  let moveOp: (home: Block[], id: string, step: -1 | 1) => string | null = () => null;

  function showItemBar(item: HTMLElement | null | undefined): void {
    if (!doc) return;
    const itemId = item?.dataset['i'];
    const owner = item?.closest<HTMLElement>('[data-b]');
    const blockId = owner?.dataset['b'];
    if (itemBar && itemBar.dataset['for'] === `${blockId}/${itemId}`) return;
    itemBar?.remove();
    itemBar = null;
    shownItem = item ?? null;
    if (!item || !itemId || !blockId) return;
    const op = (text: string, title: string, kind: 'duplicate' | 'remove' | 'up' | 'down') =>
      el('button', {
        type: 'button',
        text,
        title,
        onmousedown: (e: Event) => e.preventDefault(),
        onclick: (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          host.itemOp(blockId, itemId, kind);
        }
      });
    itemBar = el('div', { class: 'fg-ui fg-bar', 'data-for': `${blockId}/${itemId}` }, op('⧉', 'Çoğalt (yeni öğe)', 'duplicate'), op('↑', 'Öne', 'up'), op('↓', 'Arkaya', 'down'), op('✕', 'Sil', 'remove'));
    doc.body.append(itemBar);
    position(itemBar, item.getBoundingClientRect(), 'above-right');
  }

  /* ── Sürükle-bırak ───────────────────────────────────── */

  function dragMove(e: MouseEvent): void {
    if (!doc || !drag) return;
    const under = doc.elementFromPoint(e.clientX, e.clientY);
    const target = under?.closest<HTMLElement>('[data-b]');
    const dragged = doc.querySelector(`[data-b="${CSS.escape(drag.id)}"]`);
    drop?.remove();
    drop = null;
    /* Fare bir sütunun boş alanında (hiçbir bloğun üstünde değil): sütunun
       sonuna bırak. Boş bir sütuna blok taşımanın tek yolu bu. */
    const column = under?.closest<HTMLElement>('[data-col]');
    if (column && dragged && !dragged.contains(column) && (!target || !column.contains(target) || target === column.closest('[data-b]'))) {
      const rect = column.getBoundingClientRect();
      drag.target = `col:${column.dataset['col']}`;
      drag.after = true;
      drop = el('div', { class: 'fg-drop' });
      drop.style.left = `${rect.left + win!.scrollX}px`;
      drop.style.width = `${rect.width}px`;
      drop.style.top = `${rect.bottom + win!.scrollY - 1}px`;
      doc.body.append(drop);
      return;
    }
    if (!target || !dragged || dragged.contains(target)) {
      drag.target = null;
      return;
    }
    const rect = target.getBoundingClientRect();
    drag.target = target.dataset['b'] ?? null;
    drag.after = e.clientY > rect.top + rect.height / 2;
    drop = el('div', { class: 'fg-drop' });
    drop.style.left = `${rect.left + win!.scrollX}px`;
    drop.style.width = `${rect.width}px`;
    drop.style.top = `${(drag.after ? rect.bottom : rect.top) + win!.scrollY - 1}px`;
    doc.body.append(drop);
  }

  function endDrag(): void {
    if (!drag || !doc) return;
    const { id, target, after } = drag;
    drag = null;
    drop?.remove();
    drop = null;
    doc.body.style.cursor = '';
    if (target && target !== id) host.mutate((home) => (dragDropOp(home, id, target, after) ? id : null), { select: id });
  }
  let dragDropOp: (home: Block[], id: string, target: string, after: boolean) => boolean = () => false;

  /* ── Tıklama ve yerinde yazma ────────────────────────── */

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target || target.closest('.fg-ui')) return;
    if (drag) {
      endDrag();
      return;
    }
    /* Sayfanın kendi bağlantıları ve formu önizlemede çalışmıyor. */
    if (target.closest('a, [type="submit"]')) e.preventDefault();
    closeMenu();
    const blockNode = target.closest<HTMLElement>('[data-b]');
    const id = blockNode?.dataset['b'] ?? null;
    if (id !== host.selected()) host.select(id);
    const field = target.closest<HTMLElement>('[data-f]');
    if (field && id && field.closest('[data-b]') === blockNode) {
      if (editing?.el !== field) {
        startEditing(field, id, field.dataset['f']!);
        caretAtPoint(e.clientX, e.clientY);
      }
    } else if (editing) {
      stopEditing();
    }
  }

  function startEditing(node: HTMLElement, blockId: string, path: string): void {
    if (editing && editing.el !== node) stopEditing();
    editing = { el: node, blockId, path };
    node.contentEditable = 'true';
    node.spellcheck = true;
    node.focus({ preventScroll: true });
    node.addEventListener('input', onInput);
    placeFormatBar();
  }

  function stopEditing(): void {
    if (!editing) return;
    const e = editing;
    flush();
    e.el.removeEventListener('input', onInput);
    e.el.removeAttribute('contenteditable');
    editing = null;
    formatBar?.remove();
    formatBar = null;
  }

  function onInput(): void {
    if (!editing) return;
    clearTimeout(editing.timer);
    editing.timer = setTimeout(flush, 250);
  }

  /** Düzenlenen alanı belgeye yazar. */
  function flush(): void {
    if (!editing) return;
    clearTimeout(editing.timer);
    const block = blockOf(editing.blockId);
    const raw = editing.el.innerHTML;
    const html = isPlain(block, editing.path) ? sanitizePlain(raw) : sanitizeRich(raw);
    host.commitText(editing.blockId, editing.path, html);
  }

  function onFocusOut(e: FocusEvent): void {
    if (!editing || e.target !== editing.el) return;
    const next = e.relatedTarget as Element | null;
    if (next?.closest('.fg-ui')) return;
    stopEditing();
  }

  function caretAtPoint(x: number, y: number): void {
    if (!doc) return;
    const sel = doc.getSelection();
    const d = doc as Document & { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null };
    let range: Range | null = null;
    if (d.caretPositionFromPoint) {
      const p = d.caretPositionFromPoint(x, y);
      if (p) {
        range = doc.createRange();
        range.setStart(p.offsetNode, p.offset);
      }
    } else if (doc.caretRangeFromPoint) {
      range = doc.caretRangeFromPoint(x, y);
    }
    if (range && sel) {
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  function caretTo(node: HTMLElement, at: 'start' | 'end'): void {
    const sel = doc?.getSelection();
    if (!sel || !doc) return;
    const range = doc.createRange();
    range.selectNodeContents(node);
    range.collapse(at === 'start');
    sel.removeAllRanges();
    sel.addRange(range);
  }

  /** İmlecin alanın başında / sonunda olup olmadığı. */
  function caretAtEdge(node: HTMLElement): { start: boolean; end: boolean } {
    const sel = doc?.getSelection();
    if (!sel || !sel.rangeCount || !doc) return { start: false, end: false };
    const r = sel.getRangeAt(0);
    const before = doc.createRange();
    before.selectNodeContents(node);
    before.setEnd(r.startContainer, r.startOffset);
    const after = doc.createRange();
    after.selectNodeContents(node);
    after.setStart(r.endContainer, r.endOffset);
    return { start: before.toString().length === 0, end: after.toString().length === 0 };
  }

  /** İmleçten sonrasını alandan çıkarır ve HTML olarak döndürür. */
  function extractAfterCaret(node: HTMLElement): string {
    const sel = doc!.getSelection()!;
    const r = sel.getRangeAt(0);
    r.deleteContents();
    const tail = doc!.createRange();
    tail.selectNodeContents(node);
    tail.setStart(r.endContainer, r.endOffset);
    const frag = tail.extractContents();
    const box = doc!.createElement('div');
    box.append(frag);
    return sanitizeRich(box.innerHTML);
  }

  /* ── Klavye ──────────────────────────────────────────── */

  function onKey(e: KeyboardEvent): void {
    if (menu && handleMenuKey(e)) return;
    if (!editing) return;
    const { el: node, blockId, path } = editing;
    const block = blockOf(blockId);
    if (!block) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      stopEditing();
      return;
    }

    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      if (!isPlain(block, path)) doc!.execCommand('insertLineBreak');
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (SPLITTABLE.has(block.type) && path === 'text') {
        const after = extractAfterCaret(node);
        const before = sanitizeRich(node.innerHTML);
        const lang = host.lang();
        let newId = '';
        host.mutate(
          (home) => {
            const loc = locate(home, blockId);
            if (!loc) return null;
            setText(loc.block, lang, before);
            const taken = new Set<string>();
            home.forEach(function collect(b: Block) {
              taken.add(b.id);
            });
            newId = uniqueId(home, 'paragraf');
            loc.list.splice(loc.index + 1, 0, { id: newId, type: 'paragraph', text: { tr: lang === 'tr' ? after : '', ...(lang === 'en' && after ? { en: after } : {}) } });
            return newId;
          },
          { focus: { blockId: '', path: 'text', at: 'start' } }
        );
        if (pendingFocus) pendingFocus.blockId = newId;
        return;
      }
      if (block.type === 'list' && /\.text$/.test(path)) {
        const after = extractAfterCaret(node);
        const before = sanitizeRich(node.innerHTML);
        const itemPath = path.replace(/\.text$/, '');
        let next: string | null = null;
        host.mutate((home) => {
          const b = locate(home, blockId)?.block;
          if (b?.type !== 'list') return null;
          next = listSplit(b, itemPath, before, after);
          return blockId;
        });
        if (next && pendingFocus === null) pendingFocus = { blockId, path: `${next}.text`, at: 'start' };
        return;
      }
      if (block.type === 'table') {
        doc!.execCommand('insertLineBreak');
        return;
      }
      stopEditing();
      return;
    }

    if (e.key === 'Backspace' && node.textContent === '' && !node.querySelector('img')) {
      if (SPLITTABLE.has(block.type) && path === 'text') {
        e.preventDefault();
        const prev = previousField(node);
        host.mutate((home) => {
          const loc = locate(home, blockId);
          loc?.list.splice(loc.index, 1);
          return null;
        }, prev ? { focus: { ...prev, at: 'end' } } : { select: null });
        return;
      }
      if (block.type === 'list' && /\.text$/.test(path)) {
        e.preventDefault();
        const prev = previousField(node);
        host.mutate((home) => {
          const b = locate(home, blockId)?.block;
          if (b?.type === 'list') listRemove(b, path.replace(/\.text$/, ''));
          return blockId;
        }, prev ? { focus: { ...prev, at: 'end' } } : {});
        return;
      }
    }

    if (e.key === 'Tab' && block.type === 'table') {
      e.preventDefault();
      const cells = [...(doc!.querySelector(`[data-b="${CSS.escape(blockId)}"]`)?.querySelectorAll<HTMLElement>('[data-f]') ?? [])];
      const i = cells.indexOf(node) + (e.shiftKey ? -1 : 1);
      const next = cells[i];
      if (next) {
        startEditing(next, blockId, next.dataset['f']!);
        caretTo(next, 'end');
      }
      return;
    }

    if (e.key === '/' && block.type === 'paragraph' && node.textContent === '') {
      e.preventDefault();
      openMenu(node, blockId, blockId);
    }
  }

  function previousField(node: HTMLElement): { blockId: string; path: string } | null {
    if (!doc) return null;
    const all = [...doc.querySelectorAll<HTMLElement>('[data-f]')];
    const i = all.indexOf(node);
    for (let j = i - 1; j >= 0; j -= 1) {
      const owner = all[j]!.closest<HTMLElement>('[data-b]');
      if (owner?.dataset['b']) return { blockId: owner.dataset['b'], path: all[j]!.dataset['f']! };
    }
    return null;
  }

  /* ── Yapıştırma ──────────────────────────────────────── */

  function onPaste(e: ClipboardEvent): void {
    if (!editing || !e.clipboardData) return;
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const plain = isPlain(blockOf(editing.blockId), editing.path);
    const clean = plain
      ? sanitizePlain(text || html)
      : html
        ? sanitizeRich(html)
        : sanitizeRich(text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\r?\n/g, '<br>'));
    doc!.execCommand('insertHTML', false, clean);
    onInput();
  }

  /* ── Biçim çubuğu ────────────────────────────────────── */

  function placeFormatBar(): void {
    if (!doc || !editing) return;
    const block = blockOf(editing.blockId);
    if (!formatBar || formatBar.dataset['for'] !== `${editing.blockId}/${editing.path}`) {
      formatBar?.remove();
      formatBar = buildFormatBar(block, editing.path);
      formatBar.dataset['for'] = `${editing.blockId}/${editing.path}`;
      doc.body.append(formatBar);
    }
    position(formatBar, editing.el.getBoundingClientRect(), 'above-left');
    /* Blok çubuğu ile çakışmasın: yazarken blok çubuğu gizli. */
    blockBar?.remove();
    blockBar = null;
  }

  function buildFormatBar(block: Block | undefined, path: string): HTMLElement {
    const plain = isPlain(block, path);
    const exec = (cmd: string, value?: string) => {
      doc!.execCommand('styleWithCSS', false, cmd === 'foreColor' || cmd === 'hiliteColor' ? 'true' : 'false');
      doc!.execCommand(cmd, false, value);
      onInput();
    };
    const b = (text: string, title: string, run: () => void, style = '') =>
      el('button', { type: 'button', text, title, style, onmousedown: (e: Event) => e.preventDefault(), onclick: run });
    const sep = () => el('span', { class: 'fg-sep' });
    const bar = el('div', { class: 'fg-ui fg-bar' });
    if (plain) {
      bar.append(el('span', { class: 'fg-tag', text: 'Düz metin — biçim yok' }));
      return bar;
    }
    const color = el('input', { type: 'color', title: 'Yazı rengi', value: '#c9b28a' });
    const mark = el('input', { type: 'color', title: 'Vurgu rengi', value: '#fff1a8' });
    let saved: Range | null = null;
    const save = () => {
      const sel = doc!.getSelection();
      saved = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    };
    const restore = () => {
      const sel = doc!.getSelection();
      if (saved && sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
    };
    color.addEventListener('mousedown', save);
    mark.addEventListener('mousedown', save);
    color.addEventListener('input', () => {
      restore();
      exec('foreColor', color.value);
    });
    mark.addEventListener('input', () => {
      restore();
      exec('hiliteColor', mark.value);
    });
    const size = el(
      'select',
      { title: 'Yazı boyutu' },
      el('option', { value: '', text: 'Boyut' }),
      ...TEXT_SIZES.map((s) => el('option', { value: s, text: s.replace('t-', '').toUpperCase() })),
      el('option', { value: 'none', text: 'Normal' })
    );
    size.addEventListener('mousedown', save);
    size.addEventListener('change', () => {
      restore();
      applySize(size.value === 'none' ? null : size.value);
      size.value = '';
    });

    bar.append(
      b('B', 'Kalın (Ctrl+B)', () => exec('bold'), 'font-weight:800'),
      b('I', 'İtalik (Ctrl+I)', () => exec('italic'), 'font-style:italic'),
      b('U', 'Altı çizili (Ctrl+U)', () => exec('underline'), 'text-decoration:underline'),
      b('S', 'Üstü çizili', () => exec('strikeThrough'), 'text-decoration:line-through'),
      b('x²', 'Üst simge', () => exec('superscript')),
      b('x₂', 'Alt simge', () => exec('subscript')),
      sep(),
      b('🔗', 'Bağlantı', () => linkPrompt(bar)),
      el('label', { title: 'Yazı rengi' }, 'A', color),
      el('label', { title: 'Vurgu rengi' }, '▮', mark),
      size,
      b('⌫', 'Biçimi temizle', () => {
        exec('removeFormat');
        exec('unlink');
      })
    );

    if (block && (block.type === 'paragraph' || block.type === 'heading') && path === 'text') {
      const kind = el(
        'select',
        { title: 'Blok türü' },
        el('option', { value: 'p', text: 'Paragraf', selected: block.type === 'paragraph' }),
        ...[2, 3, 4].map((n) => el('option', { value: `h${n}`, text: `Başlık ${n}`, selected: block.type === 'heading' && block.level === n }))
      );
      kind.addEventListener('change', () => convert(block.id, kind.value));
      const align = (value: 'left' | 'center' | 'right' | 'justify', text: string) =>
        b(text, `Hizala: ${value}`, () =>
          host.mutate(
            (home) => {
              const target = locate(home, block.id)?.block;
              if (!target) return null;
              target.style = { ...(target.style ?? {}) };
              if (value === 'left') delete target.style.align;
              else target.style.align = value;
              return block.id;
            },
            { focus: { blockId: block.id, path: 'text', at: 'end' } }
          )
        );
      bar.append(sep(), kind, align('left', '⇤'), align('center', '↔'), align('right', '⇥'), align('justify', '≡'));
    }
    return bar;
  }

  function convert(id: string, kind: string): void {
    host.mutate(
      (home) => {
        const loc = locate(home, id);
        if (!loc) return null;
        const old = loc.block as Block & { text: { tr: string; en?: string } };
        const base = { id: old.id, ...(old.key ? { key: old.key } : {}), ...(old.style ? { style: old.style } : {}), ...(old.reveal ? { reveal: old.reveal } : {}) };
        loc.list[loc.index] =
          kind === 'p'
            ? { ...base, type: 'paragraph', text: old.text }
            : { ...base, type: 'heading', level: Number(kind.slice(1)) as 2 | 3 | 4, text: old.text };
        return id;
      },
      { focus: { blockId: id, path: 'text', at: 'end' } }
    );
  }

  /** Seçimi bir boyut sınıfıyla sarar; `null` seçimdeki boyutları kaldırır. */
  function applySize(cls: string | null): void {
    if (!doc || !editing) return;
    const sel = doc.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!editing.el.contains(range.commonAncestorContainer)) return;
    const frag = range.extractContents();
    frag.querySelectorAll('span').forEach((s) => {
      TEXT_SIZES.forEach((t) => s.classList.remove(t));
      if (!s.className) s.removeAttribute('class');
    });
    let node: Node = frag;
    if (cls) {
      const span = doc.createElement('span');
      span.className = cls;
      span.append(frag);
      node = span;
    }
    range.insertNode(node);
    sel.removeAllRanges();
    const after = doc.createRange();
    after.selectNodeContents(node.nodeType === 11 ? editing.el : (node as Element));
    sel.addRange(after);
    onInput();
  }

  function linkPrompt(bar: HTMLElement): void {
    if (!doc || !editing) return;
    const sel = doc.getSelection();
    const saved = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const existing = (sel?.anchorNode?.parentElement?.closest('a') as HTMLAnchorElement | null) ?? null;
    const input = el('input', { type: 'text', placeholder: 'https://, /sayfa, #bolum, mailto:', value: existing?.getAttribute('href') ?? '' });
    const blank = el('input', { type: 'checkbox', checked: existing?.target === '_blank' });
    const apply = () => {
      const url = input.value.trim();
      if (saved && sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
      editing?.el.focus({ preventScroll: true });
      if (!url) {
        doc!.execCommand('unlink');
      } else if (safeUrl(url)) {
        doc!.execCommand('createLink', false, url);
        doc!.querySelectorAll<HTMLAnchorElement>(`a[href="${CSS.escape(url)}"]`).forEach((a) => {
          if (blank.checked) a.target = '_blank';
          else a.removeAttribute('target');
        });
      }
      onInput();
      placeFormatBar();
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        apply();
      }
      if (e.key === 'Escape') placeFormatBar();
    });
    bar.replaceChildren(
      input,
      el('label', { title: 'Yeni sekmede aç' }, blank, ' yeni sekme'),
      el('button', { type: 'button', text: 'Uygula', onclick: apply }),
      el('button', { type: 'button', text: 'Vazgeç', onclick: () => { formatBar?.remove(); formatBar = null; placeFormatBar(); } })
    );
    input.focus();
  }

  /* ── Ekleme menüsü ───────────────────────────────────── */

  function openMenu(anchor: HTMLElement, afterId: string, replaceId?: string): void {
    if (!doc) return;
    closeMenu();
    const search = el('input', { type: 'text', placeholder: 'Blok ara…' });
    const list = el('div');
    let active = 0;
    const draw = () => {
      const q = search.value.toLocaleLowerCase('tr').trim();
      /* Arama varken ad eşleşmesi açıklama eşleşmesinden önce: "sütun"
         yazan Sütunlar'ı istiyor, açıklamasında "sütun" geçen Tablo'yu değil. */
      const score = (c: CatalogEntry) => {
        const label = c.label.toLocaleLowerCase('tr');
        if (label.startsWith(q)) return 3;
        if (label.includes(q)) return 2;
        return c.hint.toLocaleLowerCase('tr').includes(q) ? 1 : 0;
      };
      const entries = q ? CATALOG.filter((c) => score(c) > 0).sort((a, b) => score(b) - score(a)) : CATALOG;
      active = Math.min(active, Math.max(0, entries.length - 1));
      const button = (e: CatalogEntry) =>
        el(
          'button',
          {
            type: 'button',
            'data-type': e.type,
            'data-active': entries.indexOf(e) === active ? '' : null,
            onclick: () => {
              closeMenu();
              host.insert(e.type, afterId, replaceId);
            }
          },
          e.label,
          e.hint ? el('small', { text: e.hint }) : null
        );
      if (q) {
        list.replaceChildren(...(entries.length ? entries.map(button) : [el('p', { text: 'Eşleşen blok yok' })]));
      } else {
        const groups = [...new Set(entries.map((e) => e.group))];
        list.replaceChildren(...groups.flatMap((g) => [el('p', { text: g }), ...entries.filter((e) => e.group === g).map(button)]));
      }
    };
    search.addEventListener('input', () => {
      active = 0;
      draw();
    });
    menu = el('div', { class: 'fg-ui fg-menu' }, el('div', { class: 'fg-bar', style: 'margin-bottom:4px' }, search), list);
    doc.body.append(menu);
    position(menu, anchor.getBoundingClientRect(), 'below-left');
    draw();
    search.focus();
    (menu as HTMLElement & { step?: (d: number) => void; pick?: () => void }).step = (d: number) => {
      const buttons = list.querySelectorAll('button');
      active = (active + d + buttons.length) % Math.max(1, buttons.length);
      draw();
    };
    (menu as HTMLElement & { pick?: () => void }).pick = () => (list.querySelector('button[data-active]') as HTMLButtonElement | null)?.click();
  }

  function handleMenuKey(e: KeyboardEvent): boolean {
    const m = menu as (HTMLElement & { step?: (d: number) => void; pick?: () => void }) | null;
    if (!m) return false;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return true;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      m.step?.(e.key === 'ArrowDown' ? 1 : -1);
      return true;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      m.pick?.();
      return true;
    }
    return false;
  }

  function closeMenu(): void {
    menu?.remove();
    menu = null;
  }

  /* ── Dışa açılanlar ──────────────────────────────────── */

  return {
    onLoad,
    /** Yeniden çizimden sonra imlecin gideceği alan. */
    focusAfterRender(f: Focus | undefined): void {
      if (f) pendingFocus = f;
    },
    /** Kayıttan önce: yazılmakta olan alanı belgeye geçir. */
    flush(): void {
      flush();
    },
    isEditing: () => editing !== null,
    refreshSelection(): void {
      markSelected();
      showBlockBar(host.selected());
    },
    /** Belge işlemleri doc.ts'te; kabuk onları buraya bağlıyor. */
    bindOps(ops: {
      duplicate: (home: Block[], id: string) => string | null;
      move: (home: Block[], id: string, step: -1 | 1) => string | null;
      dragDrop: (home: Block[], id: string, target: string, after: boolean) => boolean;
    }): void {
      duplicateOp = ops.duplicate;
      moveOp = ops.move;
      dragDropOp = ops.dragDrop;
    }
  };

  /* Kapanışın altındaki yardımcılar — `return`'den sonra tanımlı, hoisting ile. */
  function setText(block: Block, lang: 'tr' | 'en', html: string): void {
    const b = block as Block & { text?: { tr: string; en?: string } };
    if (!b.text) return;
    if (lang === 'tr') b.text = { ...b.text, tr: html };
    else b.text = html ? { ...b.text, en: html } : { tr: b.text.tr };
  }

  function uniqueId(home: Block[], prefix: string): string {
    const used = new Set<string>();
    const visit = (list: Block[]) =>
      list.forEach((b) => {
        used.add(b.id);
        const kids = b.type === 'section' ? [...b.head, ...b.blocks] : b.type === 'columns' ? b.columns.flatMap((c) => c.blocks) : b.type === 'split' ? b.blocks : [];
        visit(kids);
      });
    visit(home);
    for (let n = 1; ; n += 1) if (!used.has(`${prefix}-${n}`)) return `${prefix}-${n}`;
  }

  function listSplit(block: Extract<Block, { type: 'list' }>, itemPath: string, before: string, after: string): string | null {
    const parts = itemPath.split('.');
    let list = block.items;
    for (let i = 1; i < parts.length - 1; i += 2) {
      const child = list[Number(parts[i])]?.items;
      if (!child) break;
      list = child;
    }
    const index = Number(parts[parts.length - 1]);
    const item = list[index];
    if (!item) return null;
    item.text = { ...item.text, tr: before };
    list.splice(index + 1, 0, { text: { tr: after } });
    return [...parts.slice(0, -1), String(index + 1)].join('.');
  }

  function listRemove(block: Extract<Block, { type: 'list' }>, itemPath: string): void {
    const parts = itemPath.split('.');
    let list = block.items;
    for (let i = 1; i < parts.length - 1; i += 2) {
      const child = list[Number(parts[i])]?.items;
      if (!child) return;
      list = child;
    }
    if (list === block.items && list.length === 1) return;
    list.splice(Number(parts[parts.length - 1]), 1);
  }
}
